"use client";

/* eslint-disable @next/next/no-img-element */
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { usePromosFor } from "@/context/PromoContext";
import { GALLERY_IMAGE_SIZES } from "@/components/ProductImageGallery";
import { ProductPhotoFallback } from "@/components/ProductPhotoFallback";
import { isPlainClick } from "@/lib/historyNav";
import { feedPeekLabel, type ProductFeedStep } from "@/lib/productFeed";
import { formatPrice, getProductPriceView } from "@/lib/pricing";
import { promosForLevel } from "@/lib/promo";
import type { CustomerGroup } from "@/lib/reseller-groups";
import { canOptimizeImageHost } from "@/lib/storeImage";

/**
 * Slide pratinjau produk BERIKUTNYA/SEBELUMNYA di ujung galeri halaman produk
 * (feed geser, `productFeed.ts`). Pembeli yang menggeser lewat foto ujung
 * langsung melihat produk tujuannya — cover, nama, harga — seperti unggahan
 * berikutnya di media sosial; begitu snap mendarat di sini, halaman pindah.
 *
 * Cover diminta dengan `sizes` yang SAMA dengan galeri: slide 0 halaman
 * tujuan (cover yang sama) terambil dari cache, jadi perpindahannya tanpa
 * spinner foto. Baru dimuat saat `armed` (pembeli mendekati ujung) supaya
 * pengunjung yang tak pernah menggeser tak ikut mengunduhnya.
 *
 * Harganya jalur `HomeProductCard`: `getProductPriceView` untuk grup pembeli,
 * promo diselaraskan ke SATU sumbu level milik `ProductDetail` — di katalog
 * reseller `group` sengaja null, jadi yang tampil harga publik.
 */
export function NextProductPeek({
  step,
  href,
  group,
  storeLogoFallback,
  armed,
  opening,
  onOpen,
}: {
  step: ProductFeedStep;
  href: string;
  group: CustomerGroup;
  /** Logo toko untuk produk tanpa foto — WAJIB false di katalog reseller (white-label). */
  storeLogoFallback: boolean;
  /** Cover mulai dimuat. */
  armed: boolean;
  /** Perpindahan ke produk ini sedang berjalan. */
  opening: boolean;
  /** Pembuka yang sama dengan geseran (ber-guard di pemanggil). */
  onOpen: () => void;
}) {
  const { product, meta } = step;
  const promos = promosForLevel(usePromosFor(product), group?.slot ?? null);
  const view = getProductPriceView(product, group, promos);
  // PO yang harganya belum ditetapkan: baris harga disembunyikan (idiom kartu).
  const hidePrice = product.productType !== "ready_stock" && view.price === undefined;
  const label = feedPeekLabel(meta);
  const cover = product.images[0];
  const forward = meta.direction === "next";

  return (
    <Link
      href={href}
      prefetch={false}
      aria-label={`${label}: ${product.name}`}
      // Tap/Enter lewat pembuka yang SAMA dengan geseran (guard ganda + TopLoader);
      // klik ber-modifier (tab baru) tetap perilaku tautan biasa.
      onClick={(event) => {
        if (!isPlainClick(event)) return;
        event.preventDefault();
        onOpen();
      }}
      className="relative block h-full w-full overflow-hidden"
    >
      {cover ? (
        armed ? (
          canOptimizeImageHost(cover) ? (
            <Image
              src={cover}
              alt=""
              fill
              sizes={GALLERY_IMAGE_SIZES}
              loading="eager"
              fetchPriority="low"
              className="object-contain"
            />
          ) : (
            <img
              src={cover}
              alt=""
              loading="eager"
              fetchPriority="low"
              className="absolute inset-0 h-full w-full object-contain"
            />
          )
        ) : null
      ) : storeLogoFallback ? (
        <ProductPhotoFallback sizes={GALLERY_IMAGE_SIZES} />
      ) : null}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-4 pb-5 pt-20 text-white">
        {/* Tanpa `uppercase`: nama kategori & merek admin dibaca apa adanya. */}
        <p className="text-[12px] font-bold text-white/90">{label}</p>
        <p className="mt-1 line-clamp-2 text-base font-extrabold leading-snug">
          {product.name}
        </p>
        {hidePrice ? null : (
          <p className="mt-0.5 text-sm font-extrabold">{formatPrice(view.price)}</p>
        )}
        <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-shopee px-3.5 py-2 text-xs font-extrabold text-white shadow-sm">
          {opening ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Membuka…
            </>
          ) : forward ? (
            <>
              Lihat produk
              <ChevronRight size={14} />
            </>
          ) : (
            <>
              <ChevronLeft size={14} />
              Lihat produk
            </>
          )}
        </span>
      </div>
    </Link>
  );
}
